import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { fmtDateTimeIST } from '@/lib/format';
import { pickKeyHighlights, pickProfileTitle, listCertBadges } from '@/lib/reportProfile';
import { computeWeightedSummary } from '@/lib/reportAggregate';
import { REPORT_TOC_TAXONOMY } from '@/lib/reportConstants';
import { formatDuration, isPanelReportVisible } from '@/lib/clientReportHelpers';
import { CompanyResumeView } from '@/components/CompanyResumeView';
import { ReportMediaModal } from '@/components/ReportMediaModal';
import { ClientReportHeader, ClientReportFooter } from '@/pages/client/ClientReportShell';
import {
  MagazineReportSidebar,
  RecommendationModule,
  McqItemRow,
} from './MagazineReportParts';
import {
  ProfileSummarySection,
  SoftUnavailableSection,
  VideoBotSection,
  McqSection,
  TechAssessmentSection,
  PanelEvaluationSections,
  HrEvaluationSection,
  EvaluationSummarySection,
} from './MagazineReportSections';
import { CLIENT_ARTEFACT_UNAVAILABLE_MSG } from '@/lib/evalErrors';
import {
  Video as VideoIcon,
  ClipboardList,
  Users,
  Sparkles,
  FileText,
} from 'lucide-react';

export function MagazineReport({
  data,
  isDemo = false,
  shareToken = null,
  clientFacing = false,
  onRegenerateRecommendation = null,
}) {
  const [mcqModalOpen, setMcqModalOpen] = useState(false);
  const [techModalOpen, setTechModalOpen] = useState(false);
  const c = data.candidate;
  const cfg = data.link.cfg || { transcript_visible: true, code_editor_visible: true, mcq_percentile_min_pool: 100 };
  const link = data.link;
  const vbUnavailable = !!data.videobot?.unavailable;
  const mcqUnavailable = !!data.mcq?.unavailable;
  const techUnavailable = !!data.tech_assessment?.unavailable;
  const panelsUnavailable = !!(data.panels || []).some((p) => p?.unavailable);
  const finalUnavailable = !!data.final_report?.unavailable;
  const resumeUnavailable = !!data.company_resume?.unavailable;
  const panels = panelsUnavailable ? [] : (data.panels || []);
  const curated = vbUnavailable ? [] : (data.videobot?.responses || []);
  const companyResume = resumeUnavailable ? null : (data.company_resume || null);
  const mcqResult = mcqUnavailable ? null : (data.mcq?.result || null);
  const mcqItems = mcqUnavailable ? [] : (data.mcq?.items || []);
  const techResult = techUnavailable ? null : (data.tech_assessment?.result || null);
  const techItems = techUnavailable ? [] : (data.tech_assessment?.items || []);
  const hrEval = data.hr_evaluation || null;
  const finalReport = finalUnavailable ? null : (data.final_report || null);
  const recommendation = data.recommendation || null;
  const resumePayload = companyResume?.payload || null;
  const keyHighlights = pickKeyHighlights(resumePayload);
  const profileTitle = pickProfileTitle(resumePayload, c);
  const certBadges = listCertBadges(resumePayload);
  const weighted = computeWeightedSummary(data);
  const visiblePanels = panels.filter(isPanelReportVisible);

  const [active, setActive] = useState('opening');
  const [openVideo, setOpenVideo] = useState(null);
  const [openVideoIdx, setOpenVideoIdx] = useState(0);
  const [openPanel, setOpenPanel] = useState(null);
  const [resumeModalOpen, setResumeModalOpen] = useState(false);

  useEffect(() => {
    const opts = { rootMargin: '-30% 0px -55% 0px', threshold: 0 };
    const ob = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) setActive(e.target.id); });
    }, opts);
    document.querySelectorAll('[data-section]').forEach((el) => ob.observe(el));
    return () => ob.disconnect();
  }, [data]);

  const hasHr = !!(hrEval && (
    hrEval.overall_score != null
    || hrEval.human_overall_score != null
    || (hrEval.factors && hrEval.factors.length)
    || hrEval.feedback_pending
  ));
  // Evaluation Summary is the client-facing section for the `final_report` artefact.
  // Do not infer it from other stage scores — only render when that artefact is included.
  const showSummary = !!finalReport;
  const showRecommendation = !clientFacing;

  const toc = [];
  for (const slot of REPORT_TOC_TAXONOMY) {
    if (slot.id === 'opening') {
      toc.push({ id: 'opening', label: slot.label, tagline: slot.tagline });
    } else if (slot.id === 'videobot' && (curated.length || vbUnavailable)) {
      toc.push({ id: 'videobot', label: slot.label, tagline: slot.tagline });
    } else if (slot.id === 'mcq' && (mcqResult || mcqUnavailable)) {
      toc.push({ id: 'mcq', label: slot.label, tagline: slot.tagline });
    } else if (slot.id === 'tech-assessment' && (techResult || techUnavailable)) {
      toc.push({ id: 'tech-assessment', label: slot.label, tagline: slot.tagline });
    } else if (slot.id === 'panel' && (visiblePanels.length || panelsUnavailable)) {
      if (panelsUnavailable) {
        toc.push({ id: 'panel-unavailable', label: slot.label, tagline: slot.tagline });
      } else {
        visiblePanels.forEach((p, i) => {
          toc.push({
            id: `panel-${p.id}`,
            label: visiblePanels.length > 1 ? `Panel Evaluation · ${i + 1}` : slot.label,
            tagline: slot.tagline,
          });
        });
      }
    } else if (slot.id === 'hr-evaluation' && hasHr) {
      toc.push({ id: 'hr-evaluation', label: slot.label, tagline: slot.tagline });
    } else if (slot.id === 'final-report' && (showSummary || finalUnavailable)) {
      toc.push({ id: 'final-report', label: slot.label, tagline: slot.tagline });
    } else if (slot.id === 'recommendation' && showRecommendation) {
      toc.push({ id: 'recommendation', label: slot.label, tagline: slot.tagline });
    }
  }

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] text-[15px] leading-7" data-testid="client-report-magazine">
      {clientFacing && <ClientReportHeader issuedAt={link.issued_at} />}
      {isDemo && (
        <div className="border-b border-amber-200 bg-amber-50 px-6 py-2 text-center text-[12px] text-amber-900">
          <span className="font-semibold">Demo / Baseline Reference</span> {'\u2014'} hardcoded sample data. This URL is frozen as the
          design baseline for the live <code className="rounded bg-amber-100 px-1">/client/&lt;token&gt;/report</code> page.
        </div>
      )}

      <div className="mx-auto grid max-w-[1440px] grid-cols-1 gap-6 px-6 py-6 lg:grid-cols-[300px_1fr]">
        <MagazineReportSidebar toc={toc} active={active} link={link} />

        <main className="min-w-0 space-y-5">
          <ProfileSummarySection
            c={c}
            profileTitle={profileTitle}
            keyHighlights={keyHighlights}
            companyResume={companyResume}
            certBadges={certBadges}
            onOpenResume={() => setResumeModalOpen(true)}
          />

          {resumeUnavailable && (
            <SoftUnavailableSection
              id="resume-unavailable"
              eyebrow="Profile"
              title="Standardised resume"
              icon={FileText}
              message={data.company_resume?.message || CLIENT_ARTEFACT_UNAVAILABLE_MSG}
            />
          )}

          {vbUnavailable ? (
            <SoftUnavailableSection
              id="videobot"
              eyebrow="Self-recorded narrative"
              title="Video Bot Screening"
              icon={VideoIcon}
              message={data.videobot?.message || CLIENT_ARTEFACT_UNAVAILABLE_MSG}
            />
          ) : (
            <VideoBotSection
              curated={curated}
              onOpenVideo={(r, idx) => { setOpenVideo(r); setOpenVideoIdx(idx); }}
            />
          )}

          {mcqUnavailable ? (
            <SoftUnavailableSection
              id="mcq"
              eyebrow="Self-paced assessment"
              title="IQ Assessment"
              icon={ClipboardList}
              message={data.mcq?.message || CLIENT_ARTEFACT_UNAVAILABLE_MSG}
            />
          ) : (
            <McqSection
              mcqRaw={data.mcq || {}}
              mcqResult={mcqResult}
              mcqItems={mcqItems}
              onOpenModal={() => setMcqModalOpen(true)}
            />
          )}

          {techUnavailable ? (
            <SoftUnavailableSection
              id="tech-assessment"
              eyebrow="Self-paced assessment"
              title="Tech Assessment"
              icon={ClipboardList}
              message={data.tech_assessment?.message || CLIENT_ARTEFACT_UNAVAILABLE_MSG}
            />
          ) : (
            <TechAssessmentSection
              techRaw={data.tech_assessment || {}}
              techResult={techResult}
              techItems={techItems}
              onOpenModal={() => setTechModalOpen(true)}
            />
          )}

          {panelsUnavailable ? (
            <SoftUnavailableSection
              id="panel-unavailable"
              eyebrow="Live interview"
              title="Panel Evaluation"
              icon={Users}
              message={(data.panels || [])[0]?.message || CLIENT_ARTEFACT_UNAVAILABLE_MSG}
            />
          ) : (
            <PanelEvaluationSections
              scoredPanels={visiblePanels}
              shareToken={shareToken}
              isDemo={isDemo}
              onOpenPanel={setOpenPanel}
            />
          )}

          <HrEvaluationSection hrEval={hrEval} hasHr={hasHr} />

          {finalUnavailable ? (
            <SoftUnavailableSection
              id="final-report"
              eyebrow="Synthesis"
              title="Evaluation Summary"
              icon={Sparkles}
              message={data.final_report?.message || CLIENT_ARTEFACT_UNAVAILABLE_MSG}
            />
          ) : (
            <EvaluationSummarySection showSummary={showSummary} weighted={weighted} />
          )}

          {showRecommendation && (
            <RecommendationModule
              recommendation={recommendation}
              hrFinalCall={hrEval?.final_call}
              onRegenerate={onRegenerateRecommendation}
            />
          )}

          {!clientFacing && (
            <footer className="pt-2 text-center text-[11px] text-muted-foreground">
              {'\u00a9'} Kadel Labs {'\u00b7'} HireIQ {'\u00b7'} {isDemo ? <>Demo baseline {'\u2014'} not a live link</> : 'Confidential candidate report'}
            </footer>
          )}
        </main>
      </div>

      {clientFacing && <ClientReportFooter />}

      <Dialog open={resumeModalOpen} onOpenChange={setResumeModalOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="profile-summary-resume-modal">
          <DialogHeader>
            <DialogTitle>Standardised resume</DialogTitle>
            <DialogDescription>
              AI-generated company resume for {c.full_name}
              {companyResume?.generated_at ? ` \u00b7 Generated ${fmtDateTimeIST(companyResume.generated_at)}` : ''}
            </DialogDescription>
          </DialogHeader>
          <CompanyResumeView
            resume={companyResume ? { ...companyResume.payload, generated_at: fmtDateTimeIST(companyResume.generated_at) } : null}
            template={companyResume?.template}
            photoUrl={c.photo_url || null}
          />
        </DialogContent>
      </Dialog>

      <ReportMediaModal
        open={!!openVideo}
        onOpenChange={(v) => !v && setOpenVideo(null)}
        kind="videobot"
        questionIndex={openVideoIdx}
        durationLabel={formatDuration(openVideo?.duration_seconds)}
        questionText={openVideo?.question_text}
        transcript={openVideo?.transcript}
        summary={openVideo?.eval_summary}
        videoUrl={openVideo?.video_url}
        score={openVideo?.overall_score}
        humanScore={openVideo?.human_overall_score}
      />

      <ReportMediaModal
        open={!!openPanel}
        onOpenChange={(v) => !v && setOpenPanel(null)}
        kind="panel"
        questionText={openPanel?.label || 'Panel Evaluation'}
        transcript={openPanel?.transcript_text}
        summary={openPanel?.ai_summary || openPanel?.ai_report?.summary_narrative?.overall_assessment}
        videoUrl={openPanel?.recording_url}
        score={openPanel?.ai_report?.overall_score}
        humanScore={openPanel?.human_overall_score}
        panel={openPanel}
        shareToken={shareToken}
        isDemo={isDemo}
      />

      <Dialog open={mcqModalOpen} onOpenChange={setMcqModalOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="magazine-mcq-modal">
          <DialogHeader>
            <DialogTitle className="text-[15px] leading-6">
              IQ Assessment {'\u2014'} every question and the candidate&apos;s answer
            </DialogTitle>
          </DialogHeader>
          {mcqResult && (
            <p className="text-[12px] text-muted-foreground">
              {mcqResult.correct} of {mcqResult.total_questions} correct ({mcqResult.score_pct}%)
              {mcqItems.length > 0 ? ` \u00b7 showing ${mcqItems.length} item${mcqItems.length === 1 ? '' : 's'}.` : ''}
            </p>
          )}
          <div className="mt-2 space-y-2">
            {mcqItems.map((q, idx) => <McqItemRow key={q.question_id} item={q} idx={idx} />)}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={techModalOpen} onOpenChange={setTechModalOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="magazine-tech-modal">
          <DialogHeader>
            <DialogTitle className="text-[15px] leading-6">
              Work Assessment {'\u2014'} every question and the candidate&apos;s answer
            </DialogTitle>
          </DialogHeader>
          {techResult && (
            <p className="text-[12px] text-muted-foreground">
              {techResult.correct} of {techResult.total_questions} correct ({techResult.score_pct}%)
              {techItems.length > 0 ? ` \u00b7 showing ${techItems.length} item${techItems.length === 1 ? '' : 's'}.` : ''}
            </p>
          )}
          <div className="mt-2 space-y-2">
            {techItems.map((q, idx) => <McqItemRow key={q.question_id} item={q} idx={idx} />)}
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}
